import React, { useEffect } from 'react';
import { Form } from 'react-form';
import { useSelector, useDispatch } from 'react-redux';

import { CheckBox, Input, PhoneNumber, Select } from '../../Form';
import Img from '../../Img';
import { getClickAttrs } from '../../Analytics/gtag';
import { postNcsaReg } from '../../../actions/event-actions';
import { requiredEmail, requiredZipCode, requiredPhoneNumber } from '../../../utils/validation';
import { required } from '../../../utils/form-validation';
import { getNCSAPlayerGradYearOptions } from '../../../utils';

import styles from './ncsaCollegeRecruiting.scss';

const gradYearOptions = getNCSAPlayerGradYearOptions();
const genderOptions = [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
];

const NcsaCollegeRecruiting = (props) => {
    const { user, ncsaEventType = 'event-registration' } = props;
    const { ncsaReg: { loading, success } } = useSelector(({ events }) => events);
    const dispatch = useDispatch();

    useEffect(() => {
        if (success) {
            props.toStep('ncsaFinish');
            // don't show ncsa form anymore to successfully registered users
            localStorage.setItem(`ncsa-${user.id}`, 3);
        }
    }, [success]);

    const onRegClick = () => {
        // segment track call - mapped to GA4
        if (window && window.analytics) {
            window.analytics.track('NCSA Event Registration Sign Me Up Click', {
                userId: user.id,
                flowId: '28156',
            });
        }
        props.toStep('ncsaReg');
    };

    const handleSubmit = (values) => {
        // segment track call - mapped to GA4
        if (window && window.analytics) {
            window.analytics.track('Event Registration Page NCSA form submission', {
                userId: user.id,
                athleteEmail: values.athleteEmail,
            });
        }
        dispatch(postNcsaReg({
            ...values,
            ncsaEventType: 'event-registration',
            athleteGender: values.athleteGender === 'M' ? 'male' : 'female',
        }));
    };

    const defaultValues = {
        athleteFirstName: user?.firstName || '',
        athleteLastName: user?.lastName || '',
        athleteEmail: user?.email || '',
        athletePhone: user?.phone || '',
        athleteGender: user?.gender || '',
        parentEmail: user?.secondaryEmail || '',
        zipCode: user?.zipCode || '',
        isParent: false,
        ncsaEventType,
    };

    return (
        <div className={styles.ncsa}>
            <div className="container">
                {props.step.id === 'ncsaPromo' && (
                    <div className="row">
                        <div className="col-12 mb32">
                            <div className="text-center mb8">
                                <Img src="/img/v2/icons/icon-valid-grad.svg" className="h48" />
                            </div>
                            <div className="text-highlight2 text-dark text-center mb12">
                                Registration Complete
                            </div>
                            <div className={`${styles.text} text-center mb8`}>
                                Check your email for updates from the event provider
                            </div>
                        </div>
                        <div className="col-12 mb32">
                            <div className="border p16">
                                <div className={`${styles.heading}`}>
                                    Get recruited to play college tennis
                                </div>
                                <div className={`d-flex aic jcc mb24`}>
                                    <Img src="/img/utr_sports/utr-sports-logo.svg" className="h24 mr16" />
                                    <i className="utr utr-close mr16" />
                                    <Img src="/img/partners/ncsa.svg" className="h24" />
                                </div>
                                <div className={`${styles.text} mb24`}>
                                    We’ve partnered with NCSA College Recruiting to help student-athletes with the dream of playing in college.
                                    <ul>
                                        <li>Register to get noticed by 3500 tennis coaches</li>
                                        <li>Get a <b>free</b> recruiting assessment with an NCSA recruiting expert</li>
                                        <li>Create a <b>free</b> personalized recruiting game plan</li>
                                    </ul>
                                    Would you like to get noticed?
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-xl btn-block text-uppercase mb16"
                                    onClick={onRegClick}
                                    {...getClickAttrs({
                                        category: 'NCSA',
                                        location: 'NCSA Event Registration CTA',
                                        context: `Sign me up for NCSA Click`,
                                    })}
                                >
                                    Yes, sign me up
                                </button>
                                <div
                                    className="link clickable text-center"
                                    {...getClickAttrs({
                                        category: 'NCSA',
                                        location: 'NCSA Event Registration CTA',
                                        context: `Not now for NCSA Click`,
                                    })}
                                    onClick={() => {
                                        if (window?.analytics) {
                                            window.analytics.track('Event Registration Page NCSA not now click', {
                                                userId: user.id,
                                            });
                                        }
                                        props.onSkipClick();
                                    }}
                                >
                                    Not now
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {props.step.id === 'ncsaReg' && (
                    <div className="row">
                        <div className="col">
                            <Form onSubmit={handleSubmit} defaultValues={defaultValues}>
                                {(formApi) => {
                                    return (
                                        <form onSubmit={formApi.submitForm} id="ncsaRegForm">
                                            <div className={`${styles.text} mb16`}>To create your free NCSA profile, please provide some additional information.</div>
                                            <Input
                                                label="Player's first name"
                                                field="athleteFirstName"
                                                type="text"
                                                validate={required}
                                                themeV3
                                            />
                                            <Input
                                                label="Player's last name"
                                                field="athleteLastName"
                                                type="text"
                                                validate={required}
                                                themeV3
                                            />
                                            <Input
                                                label="Player's email address"
                                                field="athleteEmail"
                                                type="email"
                                                validate={requiredEmail}
                                                themeV3
                                                disableProfanityCheck={true}
                                            />
                                            <PhoneNumber
                                                label="Player's phone number"
                                                field="athletePhone"
                                                placeholder="(212) 555-5555"
                                                validate={requiredPhoneNumber}
                                                themeV3
                                            />
                                            <Select
                                                label="Player's Gender"
                                                field="athleteGender"
                                                options={genderOptions}
                                                attributeToSave="value"
                                                validate={required}
                                                themeV3
                                            />
                                            <Select
                                                id="gradYear"
                                                field="athleteGraduationYear"
                                                validate={required}
                                                label="High school graduation year"
                                                options={gradYearOptions}
                                                hiddenLabel={false}
                                                themeV3
                                            />
                                            <Input
                                                label="Player's zip code"
                                                field="zipCode"
                                                type="text"
                                                validate={requiredZipCode}
                                                themeV3
                                                disableProfanityCheck={true}
                                            />
                                            <Input
                                                label="Parent's first name"
                                                field="parentFirstName"
                                                type="text"
                                                validate={required}
                                                themeV3
                                            />
                                            <Input
                                                label="Parent's last name"
                                                field="parentLastName"
                                                type="text"
                                                validate={required}
                                                themeV3
                                            />
                                            <Input
                                                label="Parent's email address"
                                                field="parentEmail"
                                                type="email"
                                                validate={requiredEmail}
                                                themeV3
                                                disableProfanityCheck={true}
                                            />
                                            <PhoneNumber
                                                label="Parent's phone number"
                                                field="parentPhone"
                                                placeholder="(212) 555-5555"
                                                validate={requiredPhoneNumber}
                                                themeV3
                                            />
                                            <CheckBox
                                                id="isParent"
                                                customLabel="I am the parent of the player"
                                                field="isParent"
                                            />
                                            <div className={`${styles.text} fs12 mb16`}>By pressing ‘Register’ you agree to receive promotional and personalized marketing messages from NCSA via phone, email and recurring automated text. Consent is not a condition of any purchase. Standard message and data rates may apply.</div>
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-xl btn-block text-uppercase"
                                                disabled={loading}
                                                {...getClickAttrs({
                                                    category: 'NCSA',
                                                    context: `Event Registration Page NCSA form submission`,
                                                    location: 'NCSA Event Registration CTA',
                                                })}
                                            >
                                                Continue
                                            </button>
                                        </form>
                                    );
                                }}
                            </Form>
                        </div>
                    </div>
                )}
                {props.step.id === 'ncsaFinish' && (
                    <div className="row">
                        <div className="col-12 mb32">
                            <div className="text-center mb8">
                                <Img src="/img/v2/icons/icon-valid-grad.svg" className="h48" />
                            </div>
                            <div className="text-highlight2 text-dark text-center">
                                Success!
                            </div>
                            <div className={`${styles.text} text-center mb24`}>
                                You&apos;ll receive a welcome email from NCSA shortly.
                            </div>
                        </div>
                        <div className="col-12 mb32">
                            <button
                                type="button"
                                className="btn btn-primary btn-xl btn-block text-uppercase mb16"
                                onClick={props.toRegConfirmation}
                            >
                                Take me to my dashboard
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NcsaCollegeRecruiting;
